import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";
import {
  AgentToolResponseSchema,
  CreateAgentToolBodySchema,
  UpdateAgentToolBodySchema
} from "./schemas/agent-tool.schema";

// ============================================================================
// Usage endpoint response schemas
// ============================================================================

const UsageAgentSchema = z.object({
  id: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  role: z.string(),
  profile_picture: z.string()
});

export type UsageAgent = z.infer<typeof UsageAgentSchema>;

/**
 * What publishing a workflow would change about the parameters its agents serve.
 *
 * `syncWorkflowConfigs` runs on publish and nowhere else, while the workflow's
 * own `agentInputSchema` is re-derived from the DRAFT trigger on every save. So
 * a saved-but-unpublished parameter edit leaves the editor showing one contract
 * and every agent serving another. This is the difference, from the agents'
 * point of view: `removed` names a parameter they serve TODAY and would stop
 * serving.
 */
const AgentInputSchemaDriftSchema = z.object({
  added: z.array(z.string()),
  removed: z.array(z.string())
});

export type AgentInputSchemaDrift = z.infer<typeof AgentInputSchemaDriftSchema>;

const TaskUsageWorkflowSchema = z.object({
  id: z.string(),
  name: z.string(),
  status: z.string(),
  iconUrl: z.string().nullable()
});

export type TaskUsageWorkflow = z.infer<typeof TaskUsageWorkflowSchema>;

/**
 * ZAgentTools — Zod Validation Schemas for Agent Tool Configurations API
 *
 * CRUD routes: /api/agents/:agentId/tools
 * Usage routes: /api/agents/tools/task/:taskId/usage and /api/agents/tools/workflow/:workflowId/usage
 */
export const ZAgentTools = {
  /** GET /api/agents/:agentId/tools — list all tool configs for an agent */
  List: {
    method: "GET" as const,
    path: "/api/agents/:agentId/tools",
    PathVars: z.object({ agentId: z.string() }),
    Response: ApiSuccessSchema(z.array(AgentToolResponseSchema))
  },

  /** GET /api/agents/:agentId/tools/:toolId — get a single tool config */
  Get: {
    method: "GET" as const,
    path: "/api/agents/:agentId/tools/:toolId",
    PathVars: z.object({ agentId: z.string(), toolId: z.string() }),
    Response: ApiSuccessSchema(AgentToolResponseSchema)
  },

  /** POST /api/agents/:agentId/tools — create a tool config for an agent */
  Create: {
    method: "POST" as const,
    path: "/api/agents/:agentId/tools",
    PathVars: z.object({ agentId: z.string() }),
    Body: CreateAgentToolBodySchema,
    Response: ApiSuccessSchema(AgentToolResponseSchema)
  },

  /** PUT /api/agents/:agentId/tools/:toolId — update a tool config */
  Update: {
    method: "PUT" as const,
    path: "/api/agents/:agentId/tools/:toolId",
    PathVars: z.object({ agentId: z.string(), toolId: z.string() }),
    Body: UpdateAgentToolBodySchema,
    Response: ApiSuccessSchema(AgentToolResponseSchema)
  },

  /** DELETE /api/agents/:agentId/tools/:toolId — delete a single tool config */
  Delete: {
    method: "DELETE" as const,
    path: "/api/agents/:agentId/tools/:toolId",
    PathVars: z.object({ agentId: z.string(), toolId: z.string() }),
    Response: ApiSuccessSchema(z.object({ id: z.string(), deleted: z.boolean() }))
  },

  /**
   * DELETE /api/agents/:agentId/tools — delete all tool configs for an agent.
   * Static route — must be registered BEFORE Delete (:toolId) in NestJS.
   */
  DeleteAll: {
    method: "DELETE" as const,
    path: "/api/agents/:agentId/tools",
    PathVars: z.object({ agentId: z.string() }),
    Response: ApiSuccessSchema(z.object({ deleted: z.number() }))
  },

  // ══════════════════════════════════════════════════════════════════════════
  // Usage endpoints — check which agents/workflows reference a task or workflow
  // Migrated from /api/agent-tool-configs (iteration 10B)
  // ══════════════════════════════════════════════════════════════════════════

  /**
   * GET /api/agents/tools/task/:taskId/usage/details
   *
   * Returns agents and workflows that have this task configured as a tool.
   * Used by TaskUsageModal and DeleteTaskConfirmationModal.
   *
   * Static prefix 'tools/task' — registered BEFORE :agentId/tools routes in NestJS.
   */
  GetTaskUsageDetails: {
    method: "GET" as const,
    path: "/api/agents/tools/task/:taskId/usage/details",
    PathVars: z.object({ taskId: z.string() }),
    Response: ApiSuccessSchema(
      z.object({
        isUsed: z.boolean(),
        agents: z.array(UsageAgentSchema),
        workflows: z.array(TaskUsageWorkflowSchema)
      })
    )
  },

  /**
   * GET /api/agents/tools/workflow/:workflowId/usage
   *
   * Returns whether a workflow is used as a tool by any agent.
   * Used by workflow-canvas-header for the "In Use" badge.
   */
  GetWorkflowUsage: {
    method: "GET" as const,
    path: "/api/agents/tools/workflow/:workflowId/usage",
    PathVars: z.object({ workflowId: z.string() }),
    Response: ApiSuccessSchema(z.object({ isUsed: z.boolean(), usageCount: z.number() }))
  },

  /**
   * GET /api/agents/tools/workflow/:workflowId/usage/details
   *
   * Returns workflow usage with the list of agents that use it, and what
   * publishing would change about the parameters those agents serve.
   * Used by workflow-canvas-header to show agent details on hover.
   *
   * `agentInputSchemaDrift` is `null` — no comparison, deliberately — when no
   * agent uses the workflow, when it is not PUBLISHED, or when either graph
   * states nothing an agent trigger can be read from. A guessed verdict about
   * what agents are serving is worse than none.
   */
  GetWorkflowUsageDetails: {
    method: "GET" as const,
    path: "/api/agents/tools/workflow/:workflowId/usage/details",
    PathVars: z.object({ workflowId: z.string() }),
    Response: ApiSuccessSchema(
      z.object({
        isUsed: z.boolean(),
        usageCount: z.number(),
        agents: z.array(UsageAgentSchema),
        agentInputSchemaDrift: AgentInputSchemaDriftSchema.nullable()
      })
    )
  },

  /**
   * GET /api/agents/tools/plugin/:pluginId/usage/details
   *
   * Returns plugin usage with the list of agents that have it configured as a tool.
   * Used by MyToolView to display usage count and agent details.
   *
   * Static prefix 'tools/plugin' — registered BEFORE :agentId/tools routes in NestJS.
   */
  GetPluginUsageDetails: {
    method: "GET" as const,
    path: "/api/agents/tools/plugin/:pluginId/usage/details",
    PathVars: z.object({ pluginId: z.string() }),
    Response: ApiSuccessSchema(
      z.object({
        isUsed: z.boolean(),
        usageCount: z.number(),
        agents: z.array(UsageAgentSchema)
      })
    )
  }
} as const;
